// src/contexts/TeamContext.js
import React, { createContext, useState, useContext, useMemo } from 'react';
import { useSnackbar } from 'notistack';
import apiService from '../services/apiService';

const transformTeams = (teams) => {
  if (!Array.isArray(teams)) return [];
  return teams.map((teams) => {
    const { _id, ...rest } = teams;
    return { id: _id, ...rest };
  });
};

// Create the context
const TeamContext = createContext();

// Create a custom hook for easier access
export const useTeam = () => {
  return useContext(TeamContext);
};

// Export the provider to wrap around the app
export const TeamProvider = ({ children }) => {
  const [teams, setTeams] = useState([]);
  const { enqueueSnackbar } = useSnackbar();

  const teamsExcludeSettings = useMemo(
    () => teams.filter(({ id }) => id !== 'settings'),
    [teams]
  );

  // Function to add a new team
  const addTeam = async (team) => {
    try {
      const { data } = await apiService.post('/api/v1/team', team);
      setTeams((prevTeams) => [...prevTeams, { ...data, id: data._id }]);
      enqueueSnackbar('Add team successfully', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Add team failed', { variant: 'error' });
    }
  };

  // Function to remove a team by id
  const removeTeam = async (id) => {
    try {
      await apiService.delete(`/api/v1/team/${id}`);
      setTeams((prevTeams) => prevTeams.filter((team) => team.id !== id));
      enqueueSnackbar('Delete team successfully', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Delete team failed', { variant: 'error' });
    }
  };

  // Function to update a team
  const updateTeam = async ({ id, ...payload }) => {
    try {
      await apiService.patch(`/api/v1/team/${id}`, payload);
      setTeams((prevTeams) =>
        prevTeams.map((team) =>
          team.id === id ? { ...team, ...payload } : team
        )
      );
      enqueueSnackbar('Update team successfully', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Update team failed', { variant: 'error' });
    }
  };

  // Function to fetch teams (could be from an API)
  const fetchTeams = async () => {
    try {
      const response = await apiService.get('/api/v1/team');
      setTeams([
        ...transformTeams(response.data),
        { id: 'settings', name: 'Settings' },
      ]);
    } catch (error) {
      console.error('🚀  fetchTeams  error:', error);
    }
  };

  return (
    <TeamContext.Provider
      value={{
        teams,
        teamsExcludeSettings,
        addTeam,
        removeTeam,
        updateTeam,
        fetchTeams,
      }}>
      {children}
    </TeamContext.Provider>
  );
};
